import * as React from 'react';
import CompleteCheckbox from './CompleteCheckbox';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import EditableTitle from './EditableTitle';
import getTodoItemViewState from '../selectors/getTodoItemViewState';
import isBeingEdited from '../selectors/isBeingEdited';
import isImportant from '../utils/isImportant';
import isTaskComplete from '../utils/isTaskComplete';
import Todo from '../store/schema/Todo';
import TodoListItemInfo from './TodoListItemInfo';
import TodoStarCharm from './TodoStarCharm';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { clickTodo, updateIsEditing, enterTodo } from '../actions/todoListItemViewStateActions';
import { ControlIcons } from 'owa-control-icons';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { reorderTodos } from '../actions/dropActions';
import { FocusZone, FocusZoneDirection, IFocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { getAnchorForContextMenu } from 'owa-positioning';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { KeyboardEvent } from 'react';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { markTodoComplete, markTodoIncomplete, updateTodoSubject } from '../actions/todoActions';
import { observer } from 'mobx-react';
import { showTodoContextMenu } from '../actions/todoFolderViewStateActions';
import { star, unstar } from '../actions/todoActions';
import { TodoItemDragData } from 'owa-todo-types/lib/types/TodoItemDragData';
import { getRTLSafeKeyCode } from '@uifabric/utilities';
import { isBrowserFirefox } from 'owa-user-agent';

let styles = require('./TodoListItem.scss');
let classNames = require('classnames/bind').bind(styles);

export interface TodoListItemProps {
    todo: Todo;

    /**
     * Whether this item supports items being dropped over it.
     * @default true
     */
    canDropOver?: boolean;
}

const DRAG_X_OFFSET = -10;
const DRAG_Y_OFFSET = 20;

@observer
export default class TodoListItem extends React.Component<TodoListItemProps, {}> {
    static defaultProps = {
        canDropOver: true,
    };

    private taskItem: HTMLDivElement;
    private innerFocus: IFocusZone;

    render() {
        const todo = this.props.todo;
        const canDropOver = this.props.canDropOver;
        const todoViewState = getTodoItemViewState(todo.Id);
        const isDragOver =
            canDropOver && todoViewState.drop.isDragOver && !todoViewState.drag.isBeingDragged;
        const isEditing = isBeingEdited(todo.Id);
        const important = isImportant(todo.Importance);
        const ariaPropsRow: AriaProperties = { role: AriaRoles.row };

        return (
            <DragAndDroppable
                isDraggable={this.isDraggable}
                getDragData={this.getDragData}
                getDragPreview={this.getDragPreview}
                showDefaultNoDragImage={false}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}
                dragViewState={todoViewState.drag}
                dropViewState={todoViewState.drop}
                canDrop={this.canDrop.bind(this)}
                onDrop={this.onDrop.bind(this)}
                canDrag={() => !isEditing}>
                <div
                    ref={ref => (this.taskItem = ref)}
                    className={styles.taskItem}
                    onContextMenu={this.onItemContextMenu}
                    onKeyDown={this.onKeyDown}
                    data-is-focusable={true}
                    {...generateDomPropertiesForAria(ariaPropsRow)}>
                    <FocusZone
                        direction={FocusZoneDirection.horizontal}
                        componentRef={ref => (this.innerFocus = ref)}>
                        <div
                            className={classNames(
                                styles.body,
                                { isEditing: isEditing },
                                { topBorderDragOver: isDragOver }
                            )}
                            onClick={this.onClick}>
                            <Icon className={styles.bar1} iconName={ControlIcons.MoreVertical} />
                            <Icon className={styles.bar2} iconName={ControlIcons.MoreVertical} />
                            <CompleteCheckbox
                                isComplete={isTaskComplete(todo.Status)}
                                onToggleComplete={this.toggleComplete.bind(this, todo)}
                            />
                            <div className={styles.titleWrapper}>
                                <EditableTitle
                                    isEditing={isEditing}
                                    title={todo.Subject}
                                    titleStyle={classNames(
                                        {
                                            completedTitle: isTaskComplete(todo.Status),
                                        },
                                        styles.title
                                    )}
                                    textFieldProps={{
                                        maxLength: 255,
                                    }}
                                    onUpdateIsEditing={this.onUpdateIsEditing}
                                    onTextFieldKeyDown={this.onTextFieldKeyDown}
                                    onUpdateTitle={(subject: string) => {
                                        if (todo.Subject !== subject) {
                                            updateTodoSubject(todo.Id, subject);
                                        }
                                    }}
                                />
                                <TodoListItemInfo todo={todo} />
                            </div>
                            <TodoStarCharm
                                onClick={!important ? () => star(todo.Id) : () => unstar(todo.Id)}
                                isImportant={important}
                                style={classNames(
                                    !important && styles.normalImportance,
                                    styles.charm
                                )}
                            />
                        </div>
                    </FocusZone>
                </div>
            </DragAndDroppable>
        );
    }

    private onUpdateIsEditing = (isEditing: boolean) => {
        updateIsEditing(this.props.todo.Id, isEditing);
    };

    public isDraggable = () => {
        /**
         * In Firefox, when a textarea is draggable, you are no longer able to click
         * to move the cursor within a textarea. This bug is tracked here: https://bugzilla.mozilla.org/show_bug.cgi?id=943944
         * A textarea is rendered by the EditableTitle component when isEditing is false,
         * so when isEditing, we want to disable dragging for the todo.
         */
        const todo = this.props.todo;
        const isEditing = isBeingEdited(todo.Id);
        return !(isBrowserFirefox() && isEditing);
    };

    public setFocus() {
        this.taskItem.focus();
    }

    private onKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case getRTLSafeKeyCode(KeyCodes.right):
                this.innerFocus.focus();
                target.preventDefault();
                target.stopPropagation();
                break;
        }
    };

    private onTextFieldKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case KeyCodes.enter:
                target.preventDefault();
                target.stopPropagation();
                // changes isEditing to false
                enterTodo();
                /**
                 * Updating the edit mode to false causes the focus to be lost, so we
                 * have to reset the focus to the task item
                 * */
                this.setFocus();
                break;
        }
    };

    private onClick = (evt: React.MouseEvent<EventTarget>) => {
        evt.stopPropagation();
        clickTodo(this.props.todo.Id);
    };

    private getDragData = () => {
        let { todo } = this.props;
        let dragData: TodoItemDragData = {
            itemType: DraggableItemTypes.Todo,
            ewsItemIds: [todo.Id],
            subjects: [todo.Subject],
        };

        return dragData;
    };

    private onDrop = (dragData: DragData, pageX: number, pageY: number, target?: HTMLElement) => {
        switch (dragData.itemType) {
            case DraggableItemTypes.Todo:
                const taskDragData = dragData as TodoItemDragData;
                if (taskDragData.ewsItemIds && taskDragData.ewsItemIds.length === 1) {
                    reorderTodos(taskDragData.ewsItemIds[0], this.props.todo.Id);
                }
                break;
        }
    };

    private canDrop = (dragInfo: DragData): boolean => {
        return this.props.canDropOver && dragInfo.itemType === DraggableItemTypes.Todo;
    };

    private getDragPreview = (todoDragData: TodoItemDragData) => {
        var elem = document.createElement('div');
        elem.className = styles.dragHelperStyle;
        elem.innerText = todoDragData.subjects[0];
        return elem;
    };

    private toggleComplete(todo: Todo) {
        if (!isTaskComplete(todo.Status)) {
            markTodoComplete(todo.Id);
        } else {
            markTodoIncomplete(todo.Id);
        }
    }

    /**
     * The onContextMenu event handler which opens the context menu
     * @param evt the mouse event
     */
    private onItemContextMenu = (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        evt.preventDefault();

        showTodoContextMenu(this.props.todo.Id, getAnchorForContextMenu(evt));
    };
}
